/**
 * Drivers workspace API (WP-P4).
 *
 * Contracts: docs/revamp/changelog_WP-C.md §3 (shifts), §4 (inspections),
 * §6 (imports + invites). Everything that the backend may not have deployed
 * yet degrades through `withFallback` so the workspace still renders.
 */
import { api, API_PREFIX, withFallback } from './client'
import type {
  DriverInviteResult,
  DriverInvitesResponse,
  ImportResult,
  Inspection,
  InspectionsResponse,
  InviteAllPendingResult,
  ShiftsResponse,
} from '../../types/api'

/* ── Invites ───────────────────────────────────────────────────────────── */

const EMPTY_INVITES: DriverInvitesResponse = {
  counts: { total: 0, not_invited: 0, invited: 0, accepted: 0, logged_in: 0 },
  drivers: [],
}

/** GET /admin/driver-invites — invite funnel for the directory. */
export async function getDriverInvites(): Promise<DriverInvitesResponse> {
  return withFallback(async () => {
    const res = await api.get<DriverInvitesResponse>(`${API_PREFIX}/admin/driver-invites`)
    return res.data ?? EMPTY_INVITES
  }, EMPTY_INVITES)
}

/** POST /admin/drivers/{id}/invite */
export async function inviteDriver(driverId: string): Promise<DriverInviteResult> {
  const res = await api.post<DriverInviteResult>(`${API_PREFIX}/admin/drivers/${driverId}/invite`)
  return res.data
}

/** POST /admin/drivers/invite-all-pending */
export async function inviteAllPendingDrivers(): Promise<InviteAllPendingResult> {
  const res = await api.post<InviteAllPendingResult>(`${API_PREFIX}/admin/drivers/invite-all-pending`)
  return res.data
}

/* ── CSV import ────────────────────────────────────────────────────────── */

export interface DriverImportOptions {
  /** file header → target field. Omit to let the server auto-map aliases. */
  mapping?: Record<string, string>
  updateExisting?: boolean
  sendInvites?: boolean
}

/**
 * POST /admin/drivers/import — multipart CSV/XLSX upload.
 * Valid rows commit even when others fail; row errors come back in `errors`.
 */
export async function importDrivers(file: File, opts: DriverImportOptions = {}): Promise<ImportResult> {
  const body = new FormData()
  body.append('file', file)
  if (opts.mapping && Object.keys(opts.mapping).length > 0) body.append('mapping', JSON.stringify(opts.mapping))
  if (opts.updateExisting) body.append('update_existing', 'true')
  if (opts.sendInvites) body.append('send_invites', 'true')
  const res = await api.post<ImportResult>(`${API_PREFIX}/admin/drivers/import`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  })
  return res.data
}

/* ── Shifts (WP-C §3) ──────────────────────────────────────────────────── */

const EMPTY_SHIFTS: ShiftsResponse = { shifts: [], count: 0 }

/** GET /shifts?driver_id=&date=&status=&limit= */
export async function listShifts(params: {
  driver_id?: string
  date?: string
  status?: 'ON_DUTY' | 'ENDED'
  limit?: number
} = {}): Promise<ShiftsResponse> {
  return withFallback(async () => {
    const res = await api.get<ShiftsResponse>(`${API_PREFIX}/shifts`, { params })
    return res.data ?? EMPTY_SHIFTS
  }, EMPTY_SHIFTS)
}

/* ── Inspections / DVIR (WP-C §4) ──────────────────────────────────────── */

const EMPTY_INSPECTIONS: InspectionsResponse = { inspections: [], count: 0 }

/** GET /inspections?driver_id=&vehicle_id=&outcome=&type=&date=&limit= */
export async function listInspections(params: {
  driver_id?: string
  vehicle_id?: string
  outcome?: string
  type?: 'PRE_TRIP' | 'POST_TRIP'
  date?: string
  limit?: number
} = {}): Promise<InspectionsResponse> {
  return withFallback(async () => {
    const res = await api.get<InspectionsResponse>(`${API_PREFIX}/inspections`, { params })
    return res.data ?? EMPTY_INSPECTIONS
  }, EMPTY_INSPECTIONS)
}

/** GET /inspections/{id} */
export async function getInspection(id: string): Promise<Inspection> {
  const res = await api.get<Inspection>(`${API_PREFIX}/inspections/${id}`)
  return res.data
}

/* ── Driver board layers ───────────────────────────────────────────────── */

/**
 * One-shot availability layer for a date: `POST /reports/DRIVER_AVAILABILITY/generate`.
 * DISPATCHER-visible columns omit `driver_id`, so callers key on `employee_id`.
 */
export interface AvailabilityRow {
  driver_id?: string | null
  driver_name?: string | null
  employee_id?: string | null
  base_location?: string | null
  shift_start_time?: string | null
  shift_end_time?: string | null
  is_on_holiday?: boolean | null
  assigned_loads_today?: number | null
}

export async function getDriverAvailabilityForDate(date: string): Promise<AvailabilityRow[]> {
  return withFallback(async () => {
    const res = await api.post<{ data?: AvailabilityRow[] }>(
      `${API_PREFIX}/reports/DRIVER_AVAILABILITY/generate`,
      { date_from: date },
    )
    return res.data?.data ?? []
  }, [])
}
